import React, { useEffect, useMemo, useState } from "react";
import { hasSupabaseConfig, supabase } from "../../lib/supabaseClient";

function fmt(num) {
  return new Intl.NumberFormat("es-PR").format(num || 0);
}

function toDayKey(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function slugify(text = "") {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function AdminMetricsDashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [postForm, setPostForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    cover: "/friday.png",
    category: "Noticias",
    status: "published",
  });
  const [postMsg, setPostMsg] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        if (!hasSupabaseConfig || !supabase) {
          throw new Error("Supabase no está configurado");
        }

        const { data, error } = await supabase
          .from("registrations")
          .select(`
            id,
            status,
            registration_type,
            created_at,
            event_categories ( name, events ( name ) )
          `)
          .order("created_at", { ascending: false })
          .limit(5000);

        if (error) throw error;
        if (!cancelled) setRows(data || []);
      } catch (err) {
        if (!cancelled) setError(err.message || "No se pudo cargar métricas");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const metrics = useMemo(() => {
    const total = rows.length;
    const pending = rows.filter((r) => r.status === "pending").length;
    const confirmed = rows.filter((r) => r.status === "confirmed").length;
    const waitlist = rows.filter((r) => r.status === "waitlist").length;
    const checkedIn = rows.filter((r) => r.status === "checked_in").length;

    const byDayMap = new Map();
    rows.forEach((r) => {
      const key = toDayKey(r.created_at);
      if (!key) return;
      byDayMap.set(key, (byDayMap.get(key) || 0) + 1);
    });

    const byDay = Array.from(byDayMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-14)
      .map(([day, count]) => ({ day, count }));

    const byEventMap = new Map();
    rows.forEach((r) => {
      const key = r.event_categories?.events?.name || "Evento sin nombre";
      byEventMap.set(key, (byEventMap.get(key) || 0) + 1);
    });

    const byEvent = Array.from(byEventMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([eventName, count]) => ({ eventName, count }));

    return { total, pending, confirmed, waitlist, checkedIn, byDay, byEvent };
  }, [rows]);

  const peak = Math.max(...metrics.byDay.map((d) => d.count), 1);

  function onPostChange(key, value) {
    setPostForm((prev) => ({ ...prev, [key]: value }));
  }

  function onTitleChange(value) {
    setPostForm((prev) => ({
      ...prev,
      title: value,
      slug: prev.slug || slugify(value),
      excerpt: prev.excerpt || `Actualización sobre ${value || "la liga"} para nuestra comunidad.`,
    }));
  }

  async function publishQuickPost(e) {
    e.preventDefault();
    setPostMsg("");

    if (!hasSupabaseConfig || !supabase) {
      setPostMsg("Configura Supabase para publicar posts.");
      return;
    }

    const cleanSlug = slugify(postForm.slug || postForm.title);
    if (!cleanSlug || !postForm.title || !postForm.content) {
      setPostMsg("Título, slug y contenido son requeridos.");
      return;
    }

    try {
      const payload = {
        slug: cleanSlug,
        title: postForm.title,
        excerpt: postForm.excerpt,
        content_md: postForm.content,
        cover_image_url: postForm.cover,
        category: postForm.category,
        status: postForm.status,
        author_name: "PR Esports League",
        published_at: postForm.status === "published" ? new Date().toISOString() : null,
      };
      const { error } = await supabase.from("blog_posts").upsert(payload, { onConflict: "slug" });
      if (error) throw error;
      setPostMsg("✅ Blog post guardado/publicado desde Dashboard.");
      setPostForm({
        title: "",
        slug: "",
        excerpt: "",
        content: "",
        cover: "/friday.png",
        category: "Noticias",
        status: "published",
      });
    } catch (err) {
      setPostMsg(`❌ ${err.message}`);
    }
  }

  return (
    <div className="min-h-screen bg-[#06070A] text-white p-6 md:p-10">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold">Admin · Dashboard de Registros</h1>
            <p className="text-slate-300 mt-1">Métricas en vivo + herramientas de contenido y medios.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <a href="/admin/registros" className="px-4 py-2 rounded-lg border border-white/20 hover:bg-white/5">Ver registros</a>
            <a href="/admin/gamergy" className="px-4 py-2 rounded-lg border border-cyan-300/30 text-cyan-200 hover:bg-cyan-400/10">Gamergy Ops</a>
            <a href="/admin/blog" className="px-4 py-2 rounded-lg border border-white/20 hover:bg-white/5">Admin blog</a>
            <a href="/admin/media" className="px-4 py-2 rounded-lg border border-cyan-300/30 text-cyan-200 hover:bg-cyan-400/10">Directorio PREL</a>
            <a href="/admin/leaderboards" className="px-4 py-2 rounded-lg border border-cyan-300/30 text-cyan-200 hover:bg-cyan-400/10">Leaderboards</a>
            <a href="/" className="px-4 py-2 rounded-lg border border-white/20 hover:bg-white/5">Home</a>
          </div>
        </div>

        {error ? <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 p-3 text-rose-200">{error}</div> : null}

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {[
            ["Total", metrics.total],
            ["Pending", metrics.pending],
            ["Confirmed", metrics.confirmed],
            ["Waitlist", metrics.waitlist],
            ["Checked-in", metrics.checkedIn],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-[#111722] p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wide">{label}</div>
              <div className="text-2xl font-bold mt-2">{loading ? "..." : fmt(value)}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-white/10 bg-[#111722] p-4">
            <h2 className="font-semibold mb-3">Últimos 14 días (registros)</h2>
            <div className="space-y-2">
              {metrics.byDay.map((d) => (
                <div key={d.day}>
                  <div className="flex justify-between text-xs text-slate-300 mb-1">
                    <span>{d.day}</span>
                    <span>{d.count}</span>
                  </div>
                  <div className="h-2 rounded bg-[#0b1019] overflow-hidden">
                    <div className="h-full bg-cyan-400" style={{ width: `${(d.count / peak) * 100}%` }} />
                  </div>
                </div>
              ))}
              {!metrics.byDay.length ? <p className="text-sm text-slate-400">Sin datos todavía.</p> : null}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#111722] p-4">
            <h2 className="font-semibold mb-3">Top eventos por volumen</h2>
            <div className="space-y-2">
              {metrics.byEvent.map((e) => (
                <div key={e.eventName} className="flex items-center justify-between border-b border-white/5 pb-2">
                  <span className="text-slate-200">{e.eventName}</span>
                  <span className="text-cyan-300 font-semibold">{e.count}</span>
                </div>
              ))}
              {!metrics.byEvent.length ? <p className="text-sm text-slate-400">Sin datos todavía.</p> : null}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-white/10 bg-[#111722] p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold">Publicar blog desde Dashboard</h2>
              <a href="/admin/blog" className="text-sm text-cyan-300 hover:underline">Abrir editor completo</a>
            </div>
            <form className="space-y-2" onSubmit={publishQuickPost}>
              <input
                placeholder="Título"
                value={postForm.title}
                onChange={(e) => onTitleChange(e.target.value)}
                className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
                required
              />
              <div className="grid grid-cols-4 gap-2">
                <input
                  placeholder="Slug"
                  value={postForm.slug}
                  onChange={(e) => onPostChange("slug", slugify(e.target.value))}
                  className="col-span-3 rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
                />
                <button type="button" onClick={() => onPostChange("slug", slugify(postForm.title))} className="rounded-lg border border-white/20 text-sm">Auto</button>
              </div>
              <input
                placeholder="Excerpt"
                value={postForm.excerpt}
                onChange={(e) => onPostChange("excerpt", e.target.value)}
                className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
              />
              <textarea
                placeholder="Contenido"
                value={postForm.content}
                onChange={(e) => onPostChange("content", e.target.value)}
                rows={5}
                className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
                required
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  placeholder="Cover URL"
                  value={postForm.cover}
                  onChange={(e) => onPostChange("cover", e.target.value)}
                  className="rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
                />
                <select value={postForm.status} onChange={(e) => onPostChange("status", e.target.value)} className="rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2">
                  <option value="published">published</option>
                  <option value="draft">draft</option>
                </select>
              </div>
              <button className="w-full py-2.5 rounded-lg bg-cyan-400 text-black font-semibold">Guardar / Publicar</button>
            </form>
            {postMsg ? <p className="text-sm mt-2 text-slate-200">{postMsg}</p> : null}
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#111722] p-4">
            <h2 className="font-semibold mb-2">Directorio de Medios PREL</h2>
            <p className="text-sm text-slate-300 mb-4">
              Sube y administra assets (imágenes, videos, artes) para blog y páginas del site.
            </p>
            <div className="space-y-2 text-sm">
              <a href="/admin/media" className="block rounded-lg border border-cyan-300/30 text-cyan-200 px-3 py-2 hover:bg-cyan-400/10">Abrir Directorio PREL</a>
              <p className="text-slate-400">Recomendación de carpetas: <code>blog/</code>, <code>eventos/</code>, <code>sponsors/</code>.</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-sm text-emerald-100">
          <p className="font-semibold text-white mb-1">✅ Acceso de equipo activo</p>
          <p>
            El panel ya está protegido con login por email (magic link) + roles de <code>admin_users</code>.
            Roles disponibles: <strong>viewer</strong>, <strong>event_manager</strong> y <strong>super_admin</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}
